import React, {
    createContext,
    useContext,
    useCallback,
    useRef,
    useState,
} from 'react'

const TreeFocusContext = createContext(null)

export const useTreeFocus = () => {
    const context = useContext(TreeFocusContext)
    // Return null if not in a provider - TreeNode will handle this gracefully
    return context
}

export const TreeFocusProvider = ({ children }) => {
    const [focusedNodeId, setFocusedNodeId] = useState(null)
    const nodesRef = useRef(new Map())

    // Get all visible node IDs in DOM order
    const getVisibleNodeIds = useCallback(() => {
        const allNodes = Array.from(nodesRef.current.entries())

        // Sort by DOM position and filter visible nodes in one pass
        return allNodes
            .sort(([, a], [, b]) => {
                const pos = a.compareDocumentPosition(b)
                return pos & Node.DOCUMENT_POSITION_FOLLOWING ? -1 : 1
            })
            .filter(([, element]) => {
                const rect = element.getBoundingClientRect()
                return rect.height > 0 && rect.width > 0
            })
            .map(([nodeId]) => nodeId)
    }, [])

    const registerNode = useCallback((nodeId, element) => {
        if (element) {
            nodesRef.current.set(nodeId, element)
        }
    }, [])

    const unregisterNode = useCallback((nodeId) => {
        nodesRef.current.delete(nodeId)
    }, [])

    const focusNode = useCallback((nodeId) => {
        const element = nodesRef.current.get(nodeId)
        if (element) {
            setFocusedNodeId(nodeId)
            element.focus()
        }
    }, [])

    const moveFocus = useCallback(
        (direction) => {
            const visibleNodes = getVisibleNodeIds()
            const currentIndex = visibleNodes.indexOf(focusedNodeId)

            if (currentIndex === -1 && visibleNodes.length > 0) {
                // No current focus, focus first node
                focusNode(visibleNodes[0])
                return
            }

            const nextIndex =
                direction === 'next'
                    ? Math.min(currentIndex + 1, visibleNodes.length - 1)
                    : Math.max(currentIndex - 1, 0)

            if (nextIndex !== currentIndex) {
                focusNode(visibleNodes[nextIndex])
            }
        },
        [focusedNodeId, focusNode, getVisibleNodeIds],
    )

    const getFirstVisibleNodeId = useCallback(() => {
        const visibleNodes = getVisibleNodeIds()
        return visibleNodes.length > 0 ? visibleNodes[0] : null
    }, [getVisibleNodeIds])

    const value = {
        focusedNodeId,
        setFocusedNodeId,
        registerNode,
        unregisterNode,
        focusNode,
        moveFocus,
        getFirstVisibleNodeId,
    }

    return (
        <TreeFocusContext.Provider value={value}>
            {children}
        </TreeFocusContext.Provider>
    )
}
